import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      <SEO
        title="Sign in — Gr8Boost ERP"
        description="Sign in to your Gr8Boost ERP workspace. Multi-workspace cloud ERP with built-in AI co-pilot."
        canonical="https://gr8booster.com/login"
      />
      {/* Left: brand panel */}
      <div className="hidden lg:flex relative bg-[#0A0A0A] text-white p-12 flex-col justify-between overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,79,0,0.2) 0%, rgba(255,79,0,0) 60%)" }}
        />
        <div className="relative">
          <Link to="/roadmap" className="inline-flex items-center gap-2 group">
            <Logo size={36} dark="#FFFFFF" accent="#FF4F00" />
          </Link>
        </div>
        <div className="relative space-y-6">
          <div className="inline-flex items-center gap-2 border border-[#FF4F00] text-[#FF4F00] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em]">
            <Sparkles size={12} /> Powered by Gr8Agentic AI
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1.05]">
            The white-label ERP<br />
            <span className="text-[#FF4F00]">that runs itself.</span>
          </h1>
          <p className="text-white/70 max-w-md leading-relaxed">
            Sales, inventory, finance, HR — unified. Ask Gr8Agentic AI anything in plain English. The white-label ERP for ambitious businesses worldwide.
          </p>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">
            <Link to="/roadmap" className="hover:text-white" data-testid="link-roadmap">View the product roadmap →</Link>
          </div>
        </div>
        <div className="relative text-[11px] uppercase tracking-[0.2em] text-white/40">
          v1.0 · multi-workspace cloud platform
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden mb-10">
            <Logo size={32} />
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tighter">Sign in to Gr8Boost ERP</h2>
          <p className="text-[#495057] mt-2">Welcome back. Pick up where you left off.</p>

          <button
            type="button"
            onClick={googleLogin}
            data-testid="login-google-btn"
            className="mt-10 w-full inline-flex items-center justify-center gap-3 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] font-medium px-4 py-3 rounded-sm transition-colors duration-200"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#495057]">
            <div className="h-px flex-1 bg-[#DEE2E6]" />
            <span>or with email</span>
            <div className="h-px flex-1 bg-[#DEE2E6]" />
          </div>

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Email</span>
            <input
              type="email"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none"
            />
          </label>
          <label className="block mt-4">
            <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Password</span>
            <input
              type="password"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none"
            />
          </label>

          {err && (
            <div data-testid="login-error" className="mt-4 text-sm text-[#D90429] border border-[#D90429] bg-[#D90429]/5 px-4 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white font-medium px-4 py-3 rounded-sm transition-colors duration-200"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign in →"}
          </button>

          <div className="mt-6 text-sm text-[#495057]">
            New to Gr8Boost ERP? <Link to="/register" className="text-[#0A0A0A] font-medium underline" data-testid="link-register">Create an account</Link>
          </div>
          <div className="mt-10 text-[11px] uppercase tracking-[0.2em] text-[#495057]">
            Want a fully-seeded workspace? <Link to="/" className="text-[#0A0A0A] font-medium underline" data-testid="link-try-demo">Try the instant demo →</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path fill="#EA4335" d="M12 11v3.6h5.1c-.2 1.3-1.5 3.8-5.1 3.8-3.1 0-5.6-2.5-5.6-5.6S8.9 7.2 12 7.2c1.7 0 2.9.7 3.6 1.3l2.4-2.3C16.5 4.9 14.4 4 12 4 7.6 4 4 7.6 4 12s3.6 8 8 8c4.6 0 7.7-3.2 7.7-7.7 0-.5-.1-.9-.2-1.3H12z" />
    </svg>
  );
}
